import React, { type ReactNode } from 'react';
import { useAuth } from '../../../modules/auth/context/AuthContext';
import { ROLES } from '../../constants/roles';
import type { Role } from '../../constants/roles';

interface CanProps {
  roles: Role[];
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Componente que verifica se o usuário logado tem permissão baseada em roles
 * @param roles - Array de roles que podem acessar o conteúdo
 * @param children - Conteúdo a ser renderizado se o usuário tiver permissão
 * @param fallback - Conteúdo alternativo se o usuário não tiver permissão (opcional)
 */
export function Can({ roles, children, fallback = null }: CanProps) {
  const { user } = useAuth();

  const hasPermission = () => {
    if (!user?.role) return false;
    return roles.some((role) => user.role.toLowerCase() === role.toLowerCase());
  };

  return hasPermission() ? React.createElement(React.Fragment, null, children) : React.createElement(React.Fragment, null, fallback);
}

/**
 * Hook para verificar permissões programaticamente
 * @param roles - Array de roles que podem acessar
 * @returns boolean indicando se o usuário tem permissão
 */
export function useCan(roles: Role[]): boolean {
  const { user } = useAuth();

  if (!user?.role) return false;
  return roles.some((role) => user.role.toLowerCase() === role.toLowerCase());
}

/**
 * Hook para verificar se o usuário é admin
 * @returns boolean indicando se o usuário é admin
 */
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.role?.toLowerCase() === ROLES.ADMIN.toLowerCase();
}

/**
 * Hook para verificar se o usuário é gestor
 * @returns boolean indicando se o usuário é gestor
 */
export function useIsGestor(): boolean {
  const { user } = useAuth();
  return user?.role?.toLowerCase() === ROLES.GESTOR.toLowerCase();
}

/**
 * Hook para verificar se o usuário é usuário comum
 * @returns boolean indicando se o usuário é usuário comum
 */
export function useIsUser(): boolean {
  const { user } = useAuth();
  return user?.role?.toLowerCase() === ROLES.USER.toLowerCase();
}

/**
 * Hook para verificar se o usuário é customer
 * @returns boolean indicando se o usuário é customer
 */
export function useIsCustomer(): boolean {
  const { user } = useAuth();
  return user?.role?.toLowerCase() === ROLES.CUSTOMER.toLowerCase();
}
